import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { fetchProjectComplianceIssues } from '../../services/complianceIssuesApi.js';
import {
  complianceTypeLabel,
  getIssueComplianceType,
  getIssueDrawingReviewTarget,
  getIssueVerdict,
  issueHasDrawingReviewTarget,
  normalizeIssueStatus,
  pathTypeLabel,
  statusLabel,
  verdictLabel,
} from '../compliance-ui/complianceIssuePresenters.js';

const PanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
`;

const Stats = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  text-align: center;
  padding: 12px 16px;
  background: rgba(255,255,255,0.8);
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.1);
  min-width: 80px;
`;

const SearchBar = styled.div`
  margin: 12px 24px 0;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 14px;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #e97024;
    box-shadow: 0 0 0 3px rgba(255,107,53,0.1);
  }
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px;
`;

const Item = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: ${(p) => (p.$active ? 'rgba(233,112,36,0.08)' : 'rgba(255,255,255,0.9)')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(233,112,36,0.35)' : 'rgba(0,0,0,0.08)')};
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(233,112,36,0.25);
    transform: translateX(-2px);
  }
`;

const IssueTitle = styled.div`
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  line-height: 1.4;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
`;

const Pill = styled.span`
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${(p) => p.$bg || 'rgba(0,0,0,0.06)'};
  color: ${(p) => p.$color || '#555'};
`;

const EvidenceLine = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const EmptyPanel = styled.div`
  padding: 24px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
`;

const LinkBtn = styled.button`
  margin-top: 14px;
  padding: 8px 14px;
  border: 1px solid rgba(233, 112, 36, 0.3);
  border-radius: 8px;
  background: rgba(233, 112, 36, 0.08);
  color: #e97024;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
`;

function verdictPillStyle(verdict) {
  if (verdict === 'non_compliant') return { bg: 'rgba(239,68,68,0.12)', color: '#dc2626' };
  if (verdict === 'needs_review') return { bg: 'rgba(245,158,11,0.12)', color: '#d97706' };
  return { bg: 'rgba(107,114,128,0.12)', color: '#4b5563' };
}

export default function ReviewIssuesPanel({
  projectId,
  requirementId,
  selectedIssueId,
  onSelectIssue,
  onNavigateToCompliance,
}) {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!projectId) {
      setIssues([]);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetchProjectComplianceIssues(projectId, { limit: 100, signal: controller.signal })
      .then(({ items }) => setIssues(items))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.error('[ReviewIssuesPanel] load error:', err);
        setError(err.message || 'Failed to load compliance issues');
        setIssues([]);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [projectId]);

  const drawingIssues = useMemo(() => {
    let list = issues.filter(issueHasDrawingReviewTarget);
    if (requirementId) {
      list = list.filter((issue) => {
        const target = getIssueDrawingReviewTarget(issue);
        return target?.requirementId === requirementId;
      });
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((issue) => {
      const target = getIssueDrawingReviewTarget(issue);
      const blob = [
        issue.ncCode,
        issue.topic,
        issue.identifiedIssue,
        issue.evidenceDoc,
        target?.docName,
        target?.sheetId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [issues, requirementId, query]);

  const stats = useMemo(() => ({
    open: drawingIssues.filter((i) => normalizeIssueStatus(i.status) === 'open').length,
    nonCompliant: drawingIssues.filter((i) => getIssueVerdict(i) === 'non_compliant').length,
    total: drawingIssues.length,
  }), [drawingIssues]);

  return (
    <>
      <PanelHeader>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>Compliance on drawings</div>
        <div style={{ color: '#666', marginTop: 6, fontSize: 14 }}>
          Issues with deliverable drawing evidence — select one to load the sheet in the viewer.
        </div>
        <Stats>
          <Stat>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#e97024' }}>{stats.total}</div>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>On drawings</div>
          </Stat>
          <Stat>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#ef4444' }}>{stats.nonCompliant}</div>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Non-compliant</div>
          </Stat>
          <Stat>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#2563eb' }}>{stats.open}</div>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Open</div>
          </Stat>
        </Stats>
      </PanelHeader>

      <SearchBar>
        <Input
          placeholder="Search issues, drawing name, NC code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SearchBar>

      <List>
        {loading && <EmptyPanel>Loading compliance issues…</EmptyPanel>}
        {!loading && error && (
          <EmptyPanel>
            <EmptyTitle>Could not load issues</EmptyTitle>
            {error}
          </EmptyPanel>
        )}
        {!loading && !error && drawingIssues.length === 0 && (
          <EmptyPanel>
            <EmptyTitle>No drawing-linked issues yet</EmptyTitle>
            {requirementId
              ? 'No compliance findings for this folder reference a linked drawing PDF. Run a scan on the Compliance Tracker, or switch folders.'
              : 'Run a compliance scan first. Issues appear here when scan evidence points at a drawing PDF in a linked folder.'}
            {onNavigateToCompliance && (
              <LinkBtn type="button" onClick={onNavigateToCompliance}>
                Go to Compliance Tracker
              </LinkBtn>
            )}
          </EmptyPanel>
        )}
        {!loading && drawingIssues.map((issue) => {
          const target = getIssueDrawingReviewTarget(issue);
          const verdict = getIssueVerdict(issue);
          const vStyle = verdictPillStyle(verdict);
          return (
            <Item
              key={issue.id}
              type="button"
              $active={selectedIssueId === issue.id}
              onClick={() => onSelectIssue(issue)}
            >
              <MetaRow>
                <Pill $bg="rgba(233,112,36,0.12)" $color="#e97024">{issue.ncCode || issue.display_id || 'Issue'}</Pill>
                <Pill $bg={vStyle.bg} $color={vStyle.color}>{verdictLabel(verdict)}</Pill>
                <Pill>{statusLabel(issue.status)}</Pill>
              </MetaRow>
              <IssueTitle>{issue.topic || issue.identifiedIssue}</IssueTitle>
              <EvidenceLine>
                {target?.docName || issue.evidenceDoc}
                {target?.pageNumber ? ` · p. ${target.pageNumber}` : ''}
                {target?.location ? ` · ${target.location}` : ''}
              </EvidenceLine>
              <EvidenceLine style={{ marginTop: 4 }}>
                {complianceTypeLabel(getIssueComplianceType(issue))}
                {issue.pathType ? ` · ${pathTypeLabel(issue.pathType)}` : ''}
              </EvidenceLine>
            </Item>
          );
        })}
      </List>
    </>
  );
}
